"use strict";

/*==============================================================================
TRESTLE
THE TRESTLE JOURNEY
SLIDE ENGINE

Brand        : Diafemme
File         : TRESTLE_Journey.js
Version      : 1.0

Responsibilities
----------------
• Operate 8-slide TRESTLE Journey
• Previous / Next navigation
• Dot navigation
• Phase navigation
• Update active phase
• Update slide count
• Support keyboard navigation
• Support horizontal swipe on touch devices
• Maintain accessible active / hidden states
==============================================================================*/


(() => {


/*==============================================================================
CONFIG
==============================================================================*/

const SELECTOR =
    "[data-tr-journey]";


const SWIPE_THRESHOLD =
    45;


/*==============================================================================
INITIALIZE ALL JOURNEYS
==============================================================================*/

function initialize(){

    const journeys =
        document.querySelectorAll(
            SELECTOR
        );


    if(!journeys.length){

        return;

    }


    journeys.forEach(root => {

        initializeJourney(
            root
        );

    });


    console.info(
        "TRESTLE Journey Ready"
    );

}


/*==============================================================================
INITIALIZE SINGLE JOURNEY
==============================================================================*/

function initializeJourney(root){

    if(
        !root ||
        root.dataset.trJourneyReady === "true"
    ){

        return;

    }


    const state = {

        currentIndex: 0,

        touchStartX: null,

        touchStartY: null,

        touchEndX: null,

        touchEndY: null

    };


    const ui = {

        root,

        track:
            root.querySelector(
                "[data-tr-track]"
            ),

        slides:
            Array.from(
                root.querySelectorAll(
                    "[data-tr-slide]"
                )
            ),

        prev:
            root.querySelector(
                "[data-tr-prev]"
            ),

        next:
            root.querySelector(
                "[data-tr-next]"
            ),

        dots:
            Array.from(
                root.querySelectorAll(
                    "[data-tr-dot]"
                )
            ),

        phases:
            Array.from(
                root.querySelectorAll(
                    "[data-tr-phase]"
                )
            ),

        count:
            root.querySelector(
                "[data-tr-count]"
            )

    };


    if(
        !ui.track ||
        !ui.slides.length
    ){

        return;

    }


    root.dataset.trJourneyReady =
        "true";


    bindNavigation(
        state,
        ui
    );


    bindDots(
        state,
        ui
    );


    bindPhases(
        state,
        ui
    );


    bindKeyboard(
        state,
        ui
    );


    bindTouch(
        state,
        ui
    );


    showSlide(
        0,
        state,
        ui,
        false
    );

}


/*==============================================================================
SHOW SLIDE
==============================================================================*/

function showSlide(
    requestedIndex,
    state,
    ui,
    focusSlide = false
){

    const total =
        ui.slides.length;


    if(!total){

        return;

    }


    const index =
        clamp(
            requestedIndex,
            0,
            total - 1
        );


    state.currentIndex =
        index;


    ui.slides.forEach(
        (slide, slideIndex) => {

            const active =
                slideIndex === index;


            slide.classList.toggle(
                "is-active",
                active
            );


            slide.setAttribute(
                "aria-hidden",
                active
                    ?
                    "false"
                    :
                    "true"
            );


            if(active){

                slide.setAttribute(
                    "tabindex",
                    "-1"
                );

            }

            else{

                slide.removeAttribute(
                    "tabindex"
                );

            }

        }
    );


    updateDots(
        index,
        ui
    );


    updatePhase(
        index,
        ui
    );


    updateCount(
        index,
        total,
        ui
    );


    updateArrows(
        index,
        total,
        ui
    );


    if(focusSlide){

        const slide =
            ui.slides[index];


        if(slide){

            slide.focus({
                preventScroll: true
            });

        }

    }

}


/*==============================================================================
PREVIOUS / NEXT
==============================================================================*/

function bindNavigation(
    state,
    ui
){

    if(ui.prev){

        ui.prev.addEventListener(
            "click",
            () => {

                showSlide(
                    state.currentIndex - 1,
                    state,
                    ui
                );

            }
        );

    }


    if(ui.next){

        ui.next.addEventListener(
            "click",
            () => {

                showSlide(
                    state.currentIndex + 1,
                    state,
                    ui
                );

            }
        );

    }

}


/*==============================================================================
DOT NAVIGATION
==============================================================================*/

function bindDots(
    state,
    ui
){

    ui.dots.forEach(
        dot => {

            dot.addEventListener(
                "click",
                () => {

                    const index =
                        Number(
                            dot.dataset.trDot
                        );


                    if(
                        Number.isNaN(
                            index
                        )
                    ){

                        return;

                    }


                    showSlide(
                        index,
                        state,
                        ui
                    );

                }
            );

        }
    );

}


/*==============================================================================
PHASE NAVIGATION
==============================================================================*/

function bindPhases(
    state,
    ui
){

    ui.phases.forEach(
        phase => {

            phase.addEventListener(
                "click",
                () => {

                    const index =
                        Number(
                            phase.dataset.trTarget
                        );


                    if(
                        Number.isNaN(
                            index
                        )
                    ){

                        return;

                    }


                    showSlide(
                        index,
                        state,
                        ui
                    );

                }
            );

        }
    );

}


/*==============================================================================
UPDATE DOTS
==============================================================================*/

function updateDots(
    activeIndex,
    ui
){

    ui.dots.forEach(
        (dot, index) => {

            const active =
                index === activeIndex;


            dot.classList.toggle(
                "is-active",
                active
            );


            dot.setAttribute(
                "aria-selected",
                active
                    ?
                    "true"
                    :
                    "false"
            );

        }
    );

}


/*==============================================================================
UPDATE PHASE
==============================================================================*/

function updatePhase(
    activeIndex,
    ui
){

    const activeSlide =
        ui.slides[
            activeIndex
        ];


    if(!activeSlide){

        return;

    }


    const activePhase =
        activeSlide.dataset.phase ||
        "";


    ui.phases.forEach(
        phase => {

            const active =
                phase.dataset.trPhase ===
                activePhase;


            phase.classList.toggle(
                "is-active",
                active
            );


            if(active){

                phase.setAttribute(
                    "aria-current",
                    "step"
                );

                keepPhaseVisible(
                    phase
                );

            }

            else{

                phase.removeAttribute(
                    "aria-current"
                );

            }

        }
    );

}


/*==============================================================================
KEEP ACTIVE PHASE VISIBLE
==============================================================================*/

function keepPhaseVisible(
    phase
){

    if(
        !phase ||
        typeof phase.scrollIntoView !==
        "function"
    ){

        return;

    }


    if(
        window.innerWidth >
        820
    ){

        return;

    }


    phase.scrollIntoView({

        behavior:
            prefersReducedMotion()
                ?
                "auto"
                :
                "smooth",

        block:
            "nearest",

        inline:
            "center"

    });

}


/*==============================================================================
COUNT
==============================================================================*/

function updateCount(
    index,
    total,
    ui
){

    if(!ui.count){

        return;

    }


    ui.count.textContent =
        pad(
            index + 1
        ) +
        " / " +
        pad(
            total
        );

}


/*==============================================================================
ARROW STATES
==============================================================================*/

function updateArrows(
    index,
    total,
    ui
){

    if(ui.prev){

        ui.prev.disabled =
            index <= 0;

    }


    if(ui.next){

        ui.next.disabled =
            index >=
            total - 1;

    }

}


/*==============================================================================
KEYBOARD
==============================================================================*/

function bindKeyboard(
    state,
    ui
){

    ui.root.addEventListener(
        "keydown",
        event => {

            if(
                event.key ===
                "ArrowLeft"
            ){

                event.preventDefault();


                showSlide(
                    state.currentIndex - 1,
                    state,
                    ui
                );

            }


            if(
                event.key ===
                "ArrowRight"
            ){

                event.preventDefault();


                showSlide(
                    state.currentIndex + 1,
                    state,
                    ui
                );

            }


            if(
                event.key ===
                "Home"
            ){

                event.preventDefault();


                showSlide(
                    0,
                    state,
                    ui
                );

            }


            if(
                event.key ===
                "End"
            ){

                event.preventDefault();


                showSlide(
                    ui.slides.length - 1,
                    state,
                    ui
                );

            }

        }
    );

}


/*==============================================================================
TOUCH / SWIPE
==============================================================================*/

function bindTouch(
    state,
    ui
){

    ui.track.addEventListener(
        "touchstart",
        event => {

            if(
                !event.touches ||
                event.touches.length !== 1
            ){

                return;

            }


            const touch =
                event.touches[0];


            state.touchStartX =
                touch.clientX;


            state.touchStartY =
                touch.clientY;


            state.touchEndX =
                null;


            state.touchEndY =
                null;

        },
        {
            passive: true
        }
    );


    ui.track.addEventListener(
        "touchmove",
        event => {

            if(
                !event.touches ||
                event.touches.length !== 1
            ){

                return;

            }


            const touch =
                event.touches[0];


            state.touchEndX =
                touch.clientX;


            state.touchEndY =
                touch.clientY;

        },
        {
            passive: true
        }
    );


    ui.track.addEventListener(
        "touchend",
        () => {

            handleSwipe(
                state,
                ui
            );

        },
        {
            passive: true
        }
    );

}


/*==============================================================================
HANDLE SWIPE
==============================================================================*/

function handleSwipe(
    state,
    ui
){

    if(
        state.touchStartX === null ||
        state.touchStartY === null ||
        state.touchEndX === null ||
        state.touchEndY === null
    ){

        resetTouch(
            state
        );

        return;

    }


    const deltaX =
        state.touchEndX -
        state.touchStartX;


    const deltaY =
        state.touchEndY -
        state.touchStartY;


    const horizontalGesture =
        Math.abs(deltaX) >
        Math.abs(deltaY);


    if(
        horizontalGesture &&
        Math.abs(deltaX) >=
        SWIPE_THRESHOLD
    ){

        if(
            deltaX < 0
        ){

            showSlide(
                state.currentIndex + 1,
                state,
                ui
            );

        }

        else{

            showSlide(
                state.currentIndex - 1,
                state,
                ui
            );

        }

    }


    resetTouch(
        state
    );

}


/*==============================================================================
RESET TOUCH
==============================================================================*/

function resetTouch(
    state
){

    state.touchStartX =
        null;

    state.touchStartY =
        null;

    state.touchEndX =
        null;

    state.touchEndY =
        null;

}


/*==============================================================================
HELPERS
==============================================================================*/

function clamp(
    value,
    min,
    max
){

    return Math.min(
        Math.max(
            value,
            min
        ),
        max
    );

}


function pad(
    value
){

    return String(
        value
    ).padStart(
        2,
        "0"
    );

}


function prefersReducedMotion(){

    return window.matchMedia &&
        window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

}


/*==============================================================================
PUBLIC API
==============================================================================*/

window.TrestleJourney = {

    initialize,

    goTo(
        journeyElement,
        index
    ){

        if(
            !journeyElement ||
            !journeyElement.matches(
                SELECTOR
            )
        ){

            return;

        }


        const slides =
            Array.from(
                journeyElement.querySelectorAll(
                    "[data-tr-slide]"
                )
            );


        const dots =
            Array.from(
                journeyElement.querySelectorAll(
                    "[data-tr-dot]"
                )
            );


        const phases =
            Array.from(
                journeyElement.querySelectorAll(
                    "[data-tr-phase]"
                )
            );


        const ui = {

            root:
                journeyElement,

            slides,

            dots,

            phases,

            prev:
                journeyElement.querySelector(
                    "[data-tr-prev]"
                ),

            next:
                journeyElement.querySelector(
                    "[data-tr-next]"
                ),

            count:
                journeyElement.querySelector(
                    "[data-tr-count]"
                )

        };


        const state = {

            currentIndex:
                0

        };


        showSlide(
            Number(index) || 0,
            state,
            ui
        );

    }

};


/*==============================================================================
BOOT
==============================================================================*/

if(
    document.readyState ===
    "loading"
){

    document.addEventListener(
        "DOMContentLoaded",
        initialize
    );

}

else{

    initialize();

}


/*==============================================================================
SHOPIFY THEME EDITOR SUPPORT
==============================================================================*/

document.addEventListener(
    "shopify:section:load",
    event => {

        const section =
            event.target;


        if(!section){

            return;

        }


        const journey =
            section.matches &&
            section.matches(
                SELECTOR
            )
            ?
            section
            :
            section.querySelector
                ?
                section.querySelector(
                    SELECTOR
                )
                :
                null;


        if(journey){

            journey.dataset.trJourneyReady =
                "false";


            initializeJourney(
                journey
            );

        }

    }
);


/*==============================================================================
END
==============================================================================*/

})();